import React, { useMemo, useState } from 'react';
import {
  ActionDrawerItems,
  ActionMenu,
  DrawerList,
  Icon,
  IconButton,
  PageHeading,
  Pills,
  Table,
  Text,
  Tooltip,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  DotsThree,
  FileText,
  Warning,
} from '@phosphor-icons/react';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import { DrawerPendencias } from './DrawerPendencias';
import type { PlanoPendencias } from './DrawerPendencias';
import './Pendencias.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type TipoPendencia = 'Taxas' | 'Markup zerado' | 'Markup negativo';

interface Pendencia {
  id: string;
  nome: string;
  planoId: string;
  qtde: number;
  tipo: TipoPendencia;
  dataCriacao: string;
  sellers: string[];
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const ALL_PENDENCIAS: Pendencia[] = [
  { id: 'P-001', nome: 'Plano Bronze',      planoId: '#00612', qtde: 13, tipo: 'Taxas', dataCriacao: '30/03/2026, 14:32', sellers: ['Loja Central LTDA', 'Mercado Bom Preço', 'Padaria Pão Quente'] },
  { id: 'P-002', nome: 'Plano Prata',       planoId: '#00455', qtde: 7,  tipo: 'Taxas', dataCriacao: '30/03/2026, 14:32', sellers: ['Tech Store', 'FitLife Academia', 'Ótica Visão Clara', 'Pet Shop Animal Love', 'Barbearia Corte Certo', 'Lavanderia Express', 'Farmácia Saúde & Vida', 'Livraria Cultura', 'Sorveteria Gelato'] },
  { id: 'P-003', nome: 'Plano Ouro',        planoId: '#00456', qtde: 1,  tipo: 'Taxas', dataCriacao: '29/03/2026, 16:20', sellers: ['Restaurante Sabor Bom'] },
  { id: 'P-004', nome: 'Plano Standard',    planoId: '#00204', qtde: 5,  tipo: 'Taxas', dataCriacao: '29/03/2026, 16:20', sellers: ['Supermercado Família', 'Auto Peças Brasil', 'Consultório Dr. Lima', 'TechStore Informática', 'Norte Distribuidora'] },
  { id: 'P-005', nome: 'Plano Premium',     planoId: '#00457', qtde: 2,  tipo: 'Taxas', dataCriacao: '29/03/2026, 10:05', sellers: ['Posto Combustível Star', 'Academia FitLife'] },
  { id: 'P-006', nome: 'Plano Starter',     planoId: '#00458', qtde: 9,  tipo: 'Taxas', dataCriacao: '28/03/2026, 15:10', sellers: ['Varejo Express', 'Loja do João', 'Mercadinho Sul'] },
  { id: 'P-007', nome: 'Plano Empresarial', planoId: '#00459', qtde: 4,  tipo: 'Taxas', dataCriacao: '28/03/2026, 15:10', sellers: ['Grupo Alfa', 'Beta Comércio', 'Gama Distribuidora', 'Delta Varejo'] },
  { id: 'P-008', nome: 'Plano Básico',      planoId: '#00460', qtde: 6,  tipo: 'Taxas', dataCriacao: '28/03/2026, 09:30', sellers: ['Mini Market', 'Padaria Central'] },
  { id: 'P-009', nome: 'Plano Plus',        planoId: '#00461', qtde: 2,  tipo: 'Taxas', dataCriacao: '27/03/2026, 15:10', sellers: ['Floricultura Rosa', 'Papelaria Ativa'] },
  { id: 'P-010', nome: 'Plano Elite',       planoId: '#00462', qtde: 8,  tipo: 'Taxas', dataCriacao: '27/03/2026, 11:45', sellers: ['Elite Store', 'Premium Shop', 'Gold Comércio', 'Silver Trade', 'Bronze Varejo'] },
  { id: 'P-011', nome: 'Plano Flex',        planoId: '#00310', qtde: 3,  tipo: 'Taxas', dataCriacao: '26/03/2026, 09:45', sellers: ['Flex Store'] },
  { id: 'P-012', nome: 'Plano Pro',         planoId: '#00521', qtde: 5,  tipo: 'Taxas', dataCriacao: '26/03/2026, 08:30', sellers: ['Pro Market', 'Profissional Shop', 'Pro Serviços'] },
  { id: 'P-013', nome: 'Plano Crescimento', planoId: '#00633', qtde: 10, tipo: 'Taxas', dataCriacao: '25/03/2026, 17:00', sellers: ['Crescer LTDA', 'Evolução Comércio', 'Up Trade', 'Next Store', 'Future Shop', 'Nova Era', 'Progresso ME'] },
  { id: 'P-014', nome: 'Plano Essencial',   planoId: '#00712', qtde: 1,  tipo: 'Taxas', dataCriacao: '25/03/2026, 14:15', sellers: ['Essencial Varejo'] },
  { id: 'P-015', nome: 'Plano Diamante',    planoId: '#00890', qtde: 7,  tipo: 'Taxas', dataCriacao: '24/03/2026, 11:20', sellers: ['Diamante Store', 'Jóias do Comércio', 'Luxo Trade', 'Premium Varejo'] },
];

// ─── Columns ──────────────────────────────────────────────────────────────────

const COLUMNS: ColumnDefinition<Pendencia>[] = [
  {
    key: 'sellers',
    title: 'Sellers impactados',
    dataIndex: 'sellers',
    width: 340,
    render: (_value, record) => {
      const sellers = record.sellers;
      if (sellers.length === 1) {
        return <Text variant="body-sm" className="pend-seller-name-truncate">{sellers[0]}</Text>;
      }
      const rest = sellers.slice(1);
      const tooltipContent = (
        <ul className="pend-tooltip-list">
          {rest.map((s) => <li key={s}>{s}</li>)}
        </ul>
      );
      return (
        <div className="pend-sellers-cell">
          <Text variant="body-sm" className="pend-seller-name-truncate">{sellers[0]}</Text>
          <Tooltip label={tooltipContent} placement="right">
            <span>
              <Pills label={`+${rest.length} sellers`} state="default" size="sm" />
            </span>
          </Tooltip>
        </div>
      );
    },
  },
  {
    key: 'descricao',
    title: 'Descrição',
    dataIndex: 'nome',
    render: (_value, record) => (
      <Text variant="body-sm">{record.nome} com {record.qtde} pendência{record.qtde !== 1 ? 's' : ''}</Text>
    ),
  },
  {
    key: 'tipo',
    title: 'Tipo de pendência',
    dataIndex: 'tipo',
    width: 220,
    render: (value) => (
      <Pills label={value as string} state="accent" variant="light" size="sm" />
    ),
  },
  {
    key: 'dataCriacao',
    title: 'Data de criação da pendência',
    dataIndex: 'dataCriacao',
    width: 230,
  },
  {
    key: 'actions',
    title: '',
    dataIndex: 'id',
    width: 48,
    render: () => (
      <div onClick={(e) => e.stopPropagation()}>
        <ActionMenu
          align="right"
          trigger={
            <IconButton variant="ghost" size="sm" aria-label="Ações">
              <Icon icon={DotsThree} size={20} weight="bold" />
            </IconButton>
          }
        >
          <DrawerList>
            <ActionDrawerItems
              title="Ações"
              items={[
                { label: 'Ver detalhes', variant: 'icon' as const, icon: <Icon icon={FileText} size={20} weight="regular" />, onClick: () => {} },
                { label: 'Resolver pendência', variant: 'icon' as const, icon: <Icon icon={Warning} size={20} weight="regular" />, onClick: () => {} },
              ]}
            />
          </DrawerList>
        </ActionMenu>
      </div>
    ),
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function Pendencias() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState<PlanoPendencias | null>(null);

  function handleRowClick(record: Pendencia) {
    setSelectedPlano({ nome: record.nome, planoId: record.planoId });
    setDrawerOpen(true);
  }

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return ALL_PENDENCIAS.slice(start, start + pageSize);
  }, [page, pageSize]);

  const totalPages = Math.ceil(ALL_PENDENCIAS.length / pageSize);

  return (
    <PortalPayfacsLayout>
      <main className="content">
        <div className="content-inner">
          <PageHeading title="Central de pendências" />

            <div className="content-table">
              <Table
                data={pagedData}
                columns={COLUMNS}
                rowKey={(r) => r.id}
                onRowClick={handleRowClick}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys),
                }}
                pagination={{
                  currentPage: page,
                  totalPages,
                  onPageChange: setPage,
                  pageSize,
                  pageSizeOptions: [10, 25, 50],
                  onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
                }}
              />
            </div>
          </div>
      </main>
      <DrawerPendencias
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        plano={selectedPlano}
      />
    </PortalPayfacsLayout>
  );
}
